import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { CountriesRequest, RequestClass } from 'acscp/api/requests';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { AppState } from 'acscp/store';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { Form } from 'acscp/components/common/form';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import React from 'react';
import formStyle from 'acscp/components/common/form.module.scss';
import loginPageFormStyle from './loginPageForms.module.scss';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { CreateAccountRequest } from 'acscp/api/requests/createAccountRequest';
import { LocaleCode } from 'acscp/translations';
import { Country } from 'acscp/api/models/country';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { RequestLoyaltySchemeAccountRequest } from 'acscp/api/requests/requestLoyaltySchemeAccountRequest';
import { CountryAndStateOfResidenceInputs } from 'acscp/components/auth/countryAndStateOfResidenceInputs';
import { StateValidationChecks } from 'acscp/components/common/helpers/stateValidationChecks';

const messages = defineMessages({
    firstNameError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.firstNameInvalid',
        defaultMessage: 'First name is required.'
    },
    surnameError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.surnameInvalid',
        defaultMessage: 'Surname is required.'
    },
    emailError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    telephoneError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.telephoneInvalid',
        defaultMessage: 'Please enter a valid telephone number.'
    },
    countryOfResidenceError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.countryOfResidenceInvalid',
        defaultMessage: 'Country of residence is required.'
    },
    stateOfResidenceError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.stateOfResidenceInvalid',
        defaultMessage: 'State is required for the selected country.'
    },
    provinceOfResidenceError: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.provinceOfResidenceInvalid',
        defaultMessage: 'Province is required for the selected country.'
    },
    firstNamePlaceholder: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.firstNamePlaceholder',
        defaultMessage: 'FIRST NAME'
    },
    surnamePlaceholder: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.surnamePlaceholder',
        defaultMessage: 'SURNAME'
    },
    emailPlaceholder: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.emailPlaceholder',
        defaultMessage: 'EMAIL ADDRESS'
    },
    telephonePlaceholder: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.telephonePlaceholder',
        defaultMessage: 'TELEPHONE'
    },
    success: {
        id: 'components.auth.requestLoyaltySchemeAccountForm.success',
        defaultMessage: 'Thank you for your request to become a loyalty scheme member, someone will be in touch shortly.'
    }
});

interface RequestLoyaltySchemeAccountFormStateProps {
    countries: Country[];
    countriesRequestState: RequestStateProps;
    accountRequestState: RequestStateProps;
    locale: LocaleCode;
}

interface RequestLoyaltySchemeAccountFormDispatchProps {
    fetchCountries: () => void;
    requestAccount:
        (firstName: string, surname: string,
         email: string, emailAddress: string,
         telephone: string,
         countryOfResidence: string, stateOfResidenceId: string) => void;
    resetRequestAccountErrors: () => void;
}

interface RequestLoyaltySchemeAccountForm {
    firstName: string;
    surname: string;
    email: string;
    emailAddress: string;
    telephone: string;
    countryOfResidence?: Country;
    stateOfResidenceId: string;
}
type RequestLoyaltySchemeAccountFormInternalState = RequestLoyaltySchemeAccountForm & {
    showErrors: boolean;
    showStateOfResidence: boolean;
};

type RequestLoyaltySchemeAccountFormProps =
    InjectedIntlProps & RequestLoyaltySchemeAccountFormStateProps & RequestLoyaltySchemeAccountFormDispatchProps;

export class RequestLoyaltySchemeAccountFormPresentational
    extends React.Component<RequestLoyaltySchemeAccountFormProps, RequestLoyaltySchemeAccountFormInternalState> {
    private validator: FormValidator;

    constructor(props: RequestLoyaltySchemeAccountFormProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = {
            firstName: '',
            surname: '',
            email: '',
            emailAddress: '',
            telephone: '',
            stateOfResidenceId: '',
            showErrors: false,
            showStateOfResidence: false
        };
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;

        return [
            {
                validatedPropertyName: 'firstName',
                validationCheck: StringValidationChecks.isRequired(() => this.state.firstName),
                errorMessage: translator(messages.firstNameError)
            },
            {
                validatedPropertyName: 'surname',
                validationCheck: StringValidationChecks.isRequired(() => this.state.surname),
                errorMessage: translator(messages.surnameError)
            },
            {
                validatedPropertyName: 'emailAddress',
                validationCheck: StringValidationChecks.isEmail(() => this.state.emailAddress),
                errorMessage: translator(messages.emailError)
            },
            {
                validatedPropertyName: 'telephone',
                validationCheck: StringValidationChecks.isRequired(() => this.state.telephone),
                errorMessage: translator(messages.telephoneError)
            },
            {
                validatedPropertyName: 'countryOfResidence',
                validationCheck: StringValidationChecks.isRequired(
                    () => this.state.countryOfResidence && this.state.countryOfResidence.id || null),
                errorMessage: translator(messages.countryOfResidenceError)
            },
            {
                validatedPropertyName: 'stateOfResidence',
                validationCheck: StateValidationChecks.isStateSelectionValidForCountry(
                    () => this.state.stateOfResidenceId,
                    () => this.state.countryOfResidence),
                errorMessage:
                    () => this.state.countryOfResidence && this.state.countryOfResidence.stateType === 'STATE'
                        ? translator(messages.stateOfResidenceError)
                        : translator(messages.provinceOfResidenceError)
            },
        ];
    }

    componentDidMount(): void {
        this.props.resetRequestAccountErrors();
        this.props.fetchCountries();
    }

    firstNameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ firstName: event.target.value })

    surnameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ surname: event.target.value })

    emailChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ email: event.target.value })

    emailAddressChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ emailAddress: event.target.value })

    telephoneChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ telephone: event.target.value })

    countryOrStateChanged = (country: Country | undefined, stateId: string) =>
        this.setState({countryOfResidence: country, stateOfResidenceId: stateId})

    requestAccount = () => {
        this.props.resetRequestAccountErrors();

        if (!this.props.accountRequestState.isLoading) {
            const { firstName, surname, email, emailAddress, telephone, countryOfResidence, stateOfResidenceId } = this.state;

            if (this.validator.isStateValid()) {
                const countryId = countryOfResidence && countryOfResidence.id || '';
                this.props.requestAccount(firstName, surname, email, emailAddress, telephone, countryId, stateOfResidenceId);
            }
        }

        this.setState({ showErrors: true });
    }

    render(): JSX.Element {
        return (
            <Form
                submit={this.requestAccount}
                header={(
                    <FormattedMessage
                        id={'components.auth.requestLoyaltySchemeAccountForm.header'}
                        defaultMessage={'BECOME A MEMBER'}
                    />
                )}
                formClassName={loginPageFormStyle.LoginPageForm}
            >
                <Link
                    to={'/login-my-rewards'}
                    className={classNames(formStyle.TextLink, formStyle.TextLinkLeftAlign)}
                >
                    <FormattedMessage
                        id={'components.auth.requestLoyaltySchemeAccountForm.back'}
                        defaultMessage={'< Back'}
                    />
                </Link>
                {this.props.accountRequestState.success
                    ? this.renderSuccessMessage()
                    : this.renderFormBody()
                }
            </Form>
        );
    }

    private renderFormBody(): JSX.Element {
        if (!this.props.countriesRequestState.isLoading && !this.props.countriesRequestState.success) {
            return <RequestErrorMessage requestState={this.props.countriesRequestState} />;
        }

        const { firstName, surname, email, emailAddress, telephone, countryOfResidence, stateOfResidenceId } = this.state;
        const { isLoading, requestError } = this.props.accountRequestState;
        const countriesListLoading = this.props.countriesRequestState.isLoading;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return (
            <>
                <FormattedMessage
                    id={'components.auth.requestLoyaltySchemeAccountForm.description'}
                    defaultMessage={
                        'Enter your details below and we’ll be in touch regarding your request. ' +
                        'Alternatively, contact your account manager to discuss becoming a member.'}
                />
                <InputWithValidation
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={firstName}
                    placeholder={this.props.intl.formatMessage(messages.firstNamePlaceholder)}
                    onChange={this.firstNameChanged}
                    validationErrors={validationErrors}
                    autoFocus={true}
                />
                <InputWithValidation
                    type="text"
                    name="surname"
                    id="surname"
                    value={surname}
                    placeholder={this.props.intl.formatMessage(messages.surnamePlaceholder)}
                    onChange={this.surnameChanged}
                    validationErrors={validationErrors}
                />
                <InputWithValidation
                    type="email"
                    name="email"
                    id="email"
                    value={email}
                    placeholder={this.props.intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.emailChanged}
                    validationErrors={validationErrors}
                    autoComplete="doNotAutoComplete"
                    tabIndex={-1}
                    inputContainerClassNames={loginPageFormStyle._EmailFieldContainer}
                />
                <InputWithValidation
                    type="email"
                    name="emailAddress"
                    id="emailAddress"
                    value={emailAddress}
                    placeholder={this.props.intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.emailAddressChanged}
                    validationErrors={validationErrors}
                />
                <InputWithValidation
                    type="tel"
                    name="telephone"
                    id="telephone"
                    value={telephone}
                    placeholder={this.props.intl.formatMessage(messages.telephonePlaceholder)}
                    onChange={this.telephoneChanged}
                    validationErrors={validationErrors}
                />
                <CountryAndStateOfResidenceInputs
                    countryInputName="countryOfResidence"
                    stateInputName="stateOfResidence"
                    validationErrors={validationErrors}
                    optionsAreLoading={countriesListLoading}
                    countryOptions={this.props.countries}
                    namingLocale={this.props.locale}
                    selectedCountry={countryOfResidence}
                    selectedStateId={stateOfResidenceId}
                    onChange={this.countryOrStateChanged}
                />
                <RequestErrorMessage requestState={this.props.accountRequestState} />
                <ButtonWithLoadingIndicator isLoading={isLoading} disabled={countriesListLoading}>
                    <FormattedMessage
                        id={'components.auth.requestLoyaltySchemeAccountForm.requestButton'}
                        defaultMessage={'REQUEST'}
                    />
                </ButtonWithLoadingIndicator>
            </>
        );
    }

    private renderSuccessMessage(): JSX.Element {
        return (
            <div>
                {this.props.intl.formatMessage(messages.success)}
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): RequestLoyaltySchemeAccountFormStateProps {
    return {
        countries: state.countries.countries,
        countriesRequestState: getRequestError(CountriesRequest),
        accountRequestState: getRequestError(RequestLoyaltySchemeAccountRequest),
        locale: state.intl.locale
    };
}

function mapDispatchToProps(dispatch: Dispatch): RequestLoyaltySchemeAccountFormDispatchProps {
    return {
        fetchCountries: () => dispatch(new FetchRequest(new CountriesRequest())),
        requestAccount: (firstName: string, surname: string,
                         email: string, emailAddress: string,
                         telephone: string,
                         countryOfResidence: string, stateOfResidenceId: string) =>
            dispatch(new FetchRequest(new RequestLoyaltySchemeAccountRequest(
                firstName, surname, email, emailAddress, telephone, countryOfResidence, stateOfResidenceId))),
        resetRequestAccountErrors: () => dispatch(new FetchResetStatus(CreateAccountRequest))
    };
}

export const RequestLoyaltySchemeAccountForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(RequestLoyaltySchemeAccountFormPresentational));
